import classNames from "classnames";
import React, { type ReactNode } from "react";

/**
 * A generic tile component for displaying named items (stages, styles, schemas, etc.)
 * with optional meta information, actions, and arbitrary children content.
 *
 * Follows breward-ui design tokens consistent with TextInput, Select and RadioGroup.
 */

interface ItemTileProps {
  /** Primary label displayed as the tile title */
  title: string;
  /** Optional meta slot — use for badges, status chips, descriptive text */
  meta?: ReactNode;
  /** Optional slot for content below the header (e.g. a Select input) */
  children?: ReactNode;
  /** Optional top-right slot for action buttons (edit, delete, etc.) */
  actions?: ReactNode;
  /** Additional CSS classes for the root element */
  className?: string;
}

export const ItemTile = ({
  title,
  meta,
  children,
  actions,
  className,
}: ItemTileProps) => {
  return (
    <div
      className={classNames(
        "relative flex min-w-0 flex-col gap-2 rounded-lg border border-solid font-sans transition-all duration-200",
        "border-primary-200/80 dark:border-primary-800/80",
        "bg-white dark:bg-content-800",
        "p-4",
        "shadow-[0_1px_3px_rgba(0,0,0,0.06)] dark:shadow-[0_1px_3px_rgba(0,0,0,0.18)]",
        "hover:border-primary-300 dark:hover:border-primary-700",
        "hover:shadow-[0_2px_6px_rgba(0,0,0,0.08)] dark:hover:shadow-[0_2px_6px_rgba(0,0,0,0.24)]",
        className,
      )}
    >
      {/* Header row: title + actions */}
      <div className="flex min-w-0 items-start justify-between gap-2">
        <div className="min-w-0 flex-1 space-y-1">
          <p
            className="truncate text-sm font-medium text-primary-900 dark:text-primary-100"
            title={title}
          >
            {title}
          </p>
          {meta && (
            <div className="flex flex-wrap items-center gap-1.5">{meta}</div>
          )}
        </div>
        {actions && (
          <div className="flex shrink-0 items-center gap-1">{actions}</div>
        )}
      </div>

      {/* Body: arbitrary children */}
      {children && <div className="min-w-0">{children}</div>}
    </div>
  );
};

ItemTile.displayName = "ItemTile";
